import React, { useContext, useState, useMemo } from 'react';

import { Popover, List, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';

import MiniLoading from '@components/Gui/MiniLoading';
import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { useStatementContext } from './StatementContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .ant-dropdown-menu-item-group-list {
        max-height: unset;
    }
`;

const Content = styled.div`
    padding: 0.25rem 0.75rem 0.5rem 0.75rem;
    min-width: 22rem;

    .ant-menu-item-group-title {
        padding: 6px 0;
    }

    .ant-list-item {
        padding: 6px 0;

        .ant-list-item-meta {
            align-items: center;
        }

        .ant-list-item-meta-title {
            margin-bottom: 0;
            text-transform: uppercase;
            font-size: 0.8rem;
            font-weight: normal;
        }
    }

    .loading-message {
        padding: 2rem;
        text-align: center;
    }
`;

/**
 * PrintStatementButton
 *
 * Universal button component for printing statements.
 * Handles two modes:
 * 1. If templates are available: shows popover with template selection and PDF generation
 * 2. If no templates: opens print drawer (onOpenPrintDrawer)
 */
const PrintStatementButton = ({
    asButton,
    dateFrom,
    dateTo,
    placement = 'bottom',
    onOpenPrintDrawer,
    statementTypeFilter = 'Real',
}) => {
    const { contactID, contactType, filters } = useStatementContext();

    const getTemplatesByType = () => {
        let areaName = '';
        switch (contactType?.toLowerCase()) {
            case 'agency':
                areaName = 'AgencyStatement';
                break;
            default:
                areaName = 'Statement';
                break;
        }
        return templates.items.filter((template) => template.area === areaName);
    };

    const { templates } = useContext(ListContext).cachedList;
    const statementTemplates = getTemplatesByType();
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [showPopover, setShowPopover] = useState(false);

    const customTemplates = useMemo(() => {
        return statementTemplates ? statementTemplates.filter((template) => template.isCustom === true) : [];
    }, [templates]);
    const hasTemplates = customTemplates && customTemplates.length > 0;

    const print = async (templateID, download) => {
        setLoadingPdf(true);
        let url = '';
        if (contactType.toLowerCase() === 'agency') {
            url = '/customers/agency/printstatement';
        } else {
            url = '/models/printstatement';
        }
        const params = {
            Template: templateID,
            DateFrom: dateFrom,
            DateTo: dateTo,
            ClientInvoiceStatus: filters.clientStatus.replace(/\s+/g, ''),
            PaymentStatus: filters.paymentStatus.replace(/\s+/g, ''),
            InvoiceStatus: filters.invoiceStatus.replace(/\s+/g, ''),
        };
        if (contactType.toLowerCase() === 'agency') {
            params.AgencyID = contactID;
        } else {
            params.ModelID = contactID;
        }
        Axios.post(url, params, { responseType: 'blob', timeout: 30000 })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                if (download) {
                    link.download = 'statement.pdf';
                }
                link.click();
                URL.revokeObjectURL(url);
                setLoadingPdf(false);
                setShowPopover(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error generating statement',
                    description: error.message,
                });
                setLoadingPdf(false);
                setShowPopover(false);
            });
    };

    const handlePrintClick = () => {
        if (hasTemplates) {
            setShowPopover(true);
        } else if (onOpenPrintDrawer) {
            onOpenPrintDrawer();
        }
    };

    // Don't render if no templates and no drawer handler
    if (!hasTemplates && !onOpenPrintDrawer) {
        return null;
    }

    // Only show for "Real" statement type filter
    if (statementTypeFilter !== 'Real') {
        return null;
    }

    // If has templates, show popover with template selection
    if (hasTemplates) {
        return (
            <Wrapper>
                <Popover
                    zIndex={9999}
                    overlayClassName="folder-popover"
                    placement={placement}
                    trigger="click"
                    open={showPopover}
                    onOpenChange={setShowPopover}
                    content={
                        <Content>
                            {loadingPdf ? (
                                <div className="loading-message">
                                    <MiniLoading size="lg" /> Generating PDF file, please wait...
                                </div>
                            ) : (
                                <>
                                    <div role="presentation" className="ant-menu-item-group-title" title="print book">
                                        SELECT A STATEMENT LAYOUT
                                    </div>
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={statementTemplates}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a key="download" onClick={() => print(item.id, true)}>
                                                        <NwIcon
                                                            tooltip="download file"
                                                            icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                                        />
                                                    </a>,
                                                    <a key="open" onClick={() => print(item.id, false)}>
                                                        <NwIcon
                                                            tooltip="open in a new window"
                                                            icon={byPrefixAndName.fasl['arrow-up-right-from-square']}
                                                        />
                                                    </a>,
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={<NwIcon icon={byPrefixAndName.fasl['file-pdf']} />}
                                                    title={item.name}
                                                    description=""
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </>
                            )}
                        </Content>
                    }
                >
                    {asButton ? (
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['print']}
                            onClick={handlePrintClick}
                            label="print statement"
                        />
                    ) : (
                        <NwIcon icon={byPrefixAndName.fasl['print']} pointer size="2x" onClick={handlePrintClick} />
                    )}
                </Popover>
            </Wrapper>
        );
    }

    // If no templates but has drawer handler, show simple button
    return asButton ? (
        <NwButton
            icon={byPrefixAndName.fasl['print']}
            ghost
            label="print statement"
            pointer
            onClick={handlePrintClick}
        />
    ) : (
        <NwIcon icon={byPrefixAndName.fasl['print']} pointer size="2x" onClick={handlePrintClick} />
    );
};

export default PrintStatementButton;
